import { buildHtml, ContentRoute, RouteSlotInput } from '@caritas/ui';
import { util } from 'zod';

import { isDefined } from '../../utils/is-defined';
import isInteger = util.isInteger;

export function mapMapBlock(
  input: BlockMapFragment,
): RouteSlotInput<typeof ContentRoute, 'body', 'map'> {

  const center =
    input.locations?.length === 1
      ? locationsCenter(input.locations)
      // If the zoom level is overridden by editors,
      // the center is calculated based on the markers and not the map bounds.
      : input.locations && input.locations?.length > 1 && isInteger(input.zoomLevel) ?
        locationsCenter(input.locations)
        : undefined;
  return {
    key: 'map',
    input: () => [
      {
        markers:
          input.locations?.filter(isDefined).map((location) => {
            return {
              position: {
                lat: location.location?.coordinates?.lat || 0,
                lng: location.location?.coordinates?.lon || 0,
              },
              Content: buildHtml(location.location?.description || ''),
            };
          }) || [],
        Title: input.injectedHeading
          ? buildHtml(input.injectedHeading)
          : undefined,
        center: center,
        // Use default value if zoomLevel is null.
        zoom: input.zoomLevel === null ? undefined : input.zoomLevel,
        mapSize: input.mapSize === null ? undefined : input.mapSize,
        centerByMapBounds: !center,
      },
      200,
    ],
  };
}

interface Coordinate {
  latitude: number;
  longitude: number;
}

// There could be more accurate implementation using Haversine formula
// https://en.wikipedia.org/wiki/Haversine_formula with Earth radius,
// but this seems to be good enough for most cases.
const locationsCenter = (locations: BlockMapFragment['locations']) => {
  if (!locations || locations.length === 0) {
    return undefined;
  }

  if (locations.length === 1) {
    return {
      lat: locations[0]?.location?.coordinates?.lat || 0,
      lng: locations[0]?.location?.coordinates?.lon || 0,
    };
  }

  const coordinates = locations.map((location) => {
    return {
      latitude: location?.location?.coordinates?.lat || 0,
      longitude: location?.location?.coordinates?.lon || 0,
    };
  });

  const center = calculateCenter(coordinates);
  return {
    lat: center.latitude,
    lng: center.longitude,
  }
};

const calculateCenter = (coordinates: Coordinate[]): Coordinate => {
  if (coordinates.length === 0) {
    console.error('No coordinates provided');
  }

  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;

  for (const coord of coordinates) {
    const latRad = toRadians(coord.latitude);
    const lonRad = toRadians(coord.longitude);

    // Convert spherical coordinates to Cartesian coordinates.
    sumX += Math.cos(latRad) * Math.cos(lonRad);
    sumY += Math.cos(latRad) * Math.sin(lonRad);
    sumZ += Math.sin(latRad);
  }

  // Calculate average Cartesian coordinates.
  const avgX = sumX / coordinates.length;
  const avgY = sumY / coordinates.length;
  const avgZ = sumZ / coordinates.length;

  // Convert average Cartesian coordinates back to spherical coordinates.
  const centerLon = Math.atan2(avgY, avgX);
  const centerLat = Math.atan2(avgZ, Math.sqrt(avgX * avgX + avgY * avgY));

  return { latitude: centerLat * (180 / Math.PI), longitude: centerLon * (180 / Math.PI) };
}

const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
}
